import { useCallback, useEffect, useRef, useState } from 'react'
import type { SupabaseClient, User } from '@supabase/supabase-js'
import type { NotificationMessage } from '@/types/notification'
import type { Scope } from '../utils/getScopeByPathname'

// ✅ Header에서 필요한 최소한의 User 정보만 사용 (타입 안전성)
type HeaderUser = Pick<User, 'id' | 'email'> | null

const POLLING_INTERVAL_MS = 30000 // 30초

export const useNotifications = (
  supabase: SupabaseClient | null,
  user: HeaderUser,
  authLoading: boolean,
  modeLoading: boolean,
  scope: Scope = 'unknown',
) => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [recentMessages, setRecentMessages] = useState<NotificationMessage[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)

  // ✅ 상태 기반 guard 대신 ref 기반 guard (클로저/렌더 타이밍 이슈 방지)
  const inFlightRef = useRef(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const reset = useCallback(() => {
    setUnreadCount(0)
    setRecentMessages([])
  }, [])

  /**
   * unreadCount 전용 API 호출 (가볍게 상시 갱신)
   */
  const loadUnreadCount = useCallback(async () => {
    if (authLoading || modeLoading || !user) {
      setUnreadCount(0)
      return
    }

    // public 스코프에서는 알림을 로드하지 않음
    if (scope === 'public' || scope === 'unknown') {
      setUnreadCount(0)
      return
    }

    try {
      const response = await fetch(
        `/api/my/notifications/unread-count?scope=${scope}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      if (!response.ok) {
        console.warn('[unread-count] 조회 실패:', response.status)
        return
      }

      const data = await response.json()
      setUnreadCount(data.unreadCount || 0)
    } catch (error) {
      console.error('[unread-count] 오류:', error)
    }
  }, [authLoading, modeLoading, user, scope])

  /**
   * 알림 리스트 로드 (호버/클릭 시에만 호출)
   */
  const loadNotifications = useCallback(async () => {
    if (authLoading || modeLoading || !user) {
      reset()
      return
    }

    // ✅ public 스코프에서는 알림을 로드하지 않음 (공개 영역)
    if (scope === 'public' || scope === 'unknown') {
      reset()
      return
    }

    // ✅ 중복 호출 방지 (ref)
    if (inFlightRef.current) return
    inFlightRef.current = true
    setLoadingMessages(true)

    try {
      const response = await fetch(
        `/api/my/notifications?scope=${scope}&limit=5`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      if (!response.ok) {
        console.warn('[notifications] 조회 실패:', response.status)
        reset()
        return
      }

      const data = await response.json()
      
      // API 응답을 NotificationMessage 형식으로 변환
      const messages: NotificationMessage[] = (data.messages || []).map((msg: any) => ({
        id: msg.id,
        type: msg.type,
        title: msg.title,
        content: msg.content,
        created_at: msg.created_at,
        status: msg.status || null,
        counsel_id: msg.counsel_id,
        notification_id: msg.notification_id || msg.id,
        payload: msg.payload,
      }))

      setRecentMessages(messages)
      // unreadCount는 별도 API로 관리하므로 여기서는 업데이트하지 않음
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      const isTokenError =
        msg.includes('token is expired') ||
        msg.includes('invalid JWT') ||
        msg.includes('unable to parse or verify signature') ||
        (error && typeof error === 'object' && 'status' in error && (error as any).status === 403)

      if (!isTokenError) console.error('[notifications] 로드 실패:', error)
      reset()
    } finally {
      setLoadingMessages(false)
      inFlightRef.current = false
    }
  }, [authLoading, modeLoading, user, reset, scope])

  /**
   * unreadCount 폴링 설정
   */
  useEffect(() => {
    // 초기 로드
    loadUnreadCount()

    // 폴링 시작
    if (scope !== 'public' && scope !== 'unknown' && user && !authLoading && !modeLoading) {
      pollingIntervalRef.current = setInterval(() => {
        loadUnreadCount()
      }, POLLING_INTERVAL_MS)
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [scope, user, authLoading, modeLoading, loadUnreadCount])

  return {
    unreadCount,
    recentMessages,
    loadingMessages,
    loadNotifications,
    setRecentMessages,
    setUnreadCount,
  }
}
